import React from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { getClassName } from 'utils/ui/classes';
import ChevronLeftIcon from './icons/ChevronLeftIcon';
import ChevronRightIcon from './icons/ChevronRightIcon';
const containerClass = 'flex items-center justify-between bg-white px-4 py-3 sm:px-6';
const paginationClass = 'relative inline-flex items-center border px-4 py-2 text-sm font-medium focus:z-20';
const mobileDevicePaginationClass = 'relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50';
const prevClass = 'relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20';
const nextClass = 'relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20';
const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';
const TRIPLE_DOT = '...';
const DIRECTION_ARRAY = ['LEFT', 'RIGHT'];
const range = (from, to, step = 1) => {
    let i = from;
    const range = [];
    while (i <= to) {
        range.push(i);
        i += step;
    }
    return range;
};
export default function Pagination({ page, perPage, total, showCount, onPageClick, className, pageNeighbours, dataTestId }) {
    const pages = Math.ceil(total / perPage);
    const { t } = useTranslation();
    const isPrevDisabled = page <= 1;
    const isNextDisabled = page >= pages;
    function handlePageClick(e) {
        const { target: { value: targetPage }, } = e;
        e.preventDefault();
        onPageClick(parseInt(targetPage, 10));
    }
    function handleMoveLeft() {
        onPageClick(page - pageNeighbours * 2 - 1);
    }
    function handleMoveRight() {
        onPageClick(page + pageNeighbours * 2 + 1);
    }
    // for Mobile device
    function clickNextPage() {
        if (page < pages) {
            onPageClick(page + 1);
        }
    }
    function clickPrevPage() {
        if (page > 1) {
            onPageClick(page - 1);
        }
    }
    function getPaginationInfo() {
        let firstCount = (page - 1) * perPage + 1;
        let lastCount = (page - 1) * perPage + perPage;
        lastCount = lastCount < total ? lastCount : total;
        return {
            firstCount,
            lastCount,
            total,
        };
    }
    function fetchPageNumbers() {
        const totalPages = pages;
        const currentPage = page;
        const totalNumbers = pageNeighbours * 2 + 3;
        const totalBlocks = totalNumbers + 2;
        if (totalPages > totalBlocks) {
            let pagesList = [];
            const leftBound = currentPage - pageNeighbours;
            const rightBound = currentPage + pageNeighbours;
            const beforeLastPage = totalPages - 1;
            const startPage = leftBound > 2 ? leftBound : 2;
            const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;
            pagesList = range(startPage, endPage);
            const pagesCount = pagesList.length;
            const singleSpillOffset = totalNumbers - pagesCount - 1;
            const leftSpill = startPage > 2;
            const rightSpill = endPage < beforeLastPage;
            const leftSpillPage = LEFT_PAGE;
            const rightSpillPage = RIGHT_PAGE;
            if (leftSpill && !rightSpill) {
                const extraPages = range(startPage - singleSpillOffset, startPage - 1);
                pagesList = [leftSpillPage, ...extraPages, ...pagesList];
            }
            else if (!leftSpill && rightSpill) {
                const extraPages = range(endPage + 1, endPage + singleSpillOffset);
                pagesList = [...pagesList, ...extraPages, rightSpillPage];
            }
            else if (leftSpill && rightSpill) {
                pagesList = [leftSpillPage, ...pagesList, rightSpillPage];
            }
            return [1, ...pagesList, totalPages];
        }
        return range(1, totalPages);
    }
    const pagesArray = fetchPageNumbers();
    return ((React.createElement("div", { "data-testid": dataTestId, className: getClassName(containerClass, className) },
        React.createElement("div", { "data-testid": "div-ecf36761", className: "flex flex-1 justify-between sm:hidden" },
            React.createElement("button", { "data-testid": "mobile-prev-btn", className: getClassName(mobileDevicePaginationClass, {
                    'cursor-not-allowed': page === 1,
                    disabled: page === 1,
                }), disabled: page === 1, onClick: clickPrevPage }, t('PREVIOUS')),
            React.createElement("button", { "data-testid": "mobile-next-btn", className: getClassName(mobileDevicePaginationClass, {
                    'cursor-not-allowed': page === pages,
                    disabled: page === pages,
                }), disabled: page === pages, onClick: clickNextPage }, t('NEXT'))),
        React.createElement("div", { "data-testid": "div-59347646", className: "hidden sm:flex sm:flex-1 sm:items-center sm:justify-between space-x-4" },
            showCount && total > 0 && (React.createElement("div", { "data-testid": "paginationInfo" },
                React.createElement("p", { "data-testid": "p-8c95b4de", className: "text-sm text-gray-700" },
                    React.createElement(Trans, { i18nKey: "PAGINATION_INFO", values: getPaginationInfo(), components: [
                            React.createElement("span", { "data-testid": "span-3c2639a0", className: "font-bold" }),
                            React.createElement("span", { "data-testid": "span-2140f981", className: "font-bold" }),
                            React.createElement("span", { "data-testid": "span-c015ac16", className: "font-bold" }),
                        ] })))),
            React.createElement("div", { "data-testid": "paginationControls" },
                React.createElement("nav", { className: "isolate inline-flex -space-x-px rounded-md shadow-sm", "aria-label": "Pagination", "data-testid": "paginationNav" },
                    pages > 1 && (React.createElement("button", { "data-testid": "prev-btn", className: getClassName(prevClass, {
                            'cursor-not-allowed': isPrevDisabled,
                            disabled: isPrevDisabled,
                        }), disabled: isPrevDisabled, onClick: clickPrevPage },
                        React.createElement("span", { "data-testid": "span-60110e85", className: "sr-only" }, t('PREVIOUS')),
                        React.createElement(ChevronLeftIcon, { className: "h-5 w-5", "aria-hidden": "true" }))),
                    pagesArray.map((currentPage) => {
                        const selected = currentPage === page;
                        if (currentPage === LEFT_PAGE) {
                            return (React.createElement("button", { "data-testid": "left-triple-dot-btn", key: currentPage, value: LEFT_PAGE, onClick: handleMoveLeft, className: getClassName(paginationClass, {
                                    'text-gray-500': !selected,
                                    'bg-white': !selected,
                                    'hover:bg-gray-50': !selected,
                                    'border-gray-300': !selected,
                                }) }, TRIPLE_DOT));
                        }
                        if (currentPage === RIGHT_PAGE) {
                            return (React.createElement("button", { "data-testid": "right-triple-dot-btn", key: currentPage, value: RIGHT_PAGE, onClick: handleMoveRight, className: getClassName(paginationClass, {
                                    'text-gray-500': !selected,
                                    'bg-white': !selected,
                                    'hover:bg-gray-50': !selected,
                                    'border-gray-300': !selected,
                                }) }, TRIPLE_DOT));
                        }
                        if (!DIRECTION_ARRAY.includes(currentPage)) {
                            return (React.createElement("button", { "data-testid": !selected ? 'page-btn-' + currentPage : 'current-page', key: currentPage, value: currentPage, onClick: handlePageClick, className: getClassName(paginationClass, {
                                    'text-gray-500': !selected,
                                    'bg-white': !selected,
                                    'hover:bg-gray-50': !selected,
                                    'border-gray-300': !selected,
                                    'text-indigo-600': selected,
                                    'bg-indigo-50': selected,
                                    'border-indigo-500': selected,
                                    'z-10': selected,
                                }) }, currentPage));
                        }
                    }),
                    pages > 1 && (React.createElement("button", { "data-testid": "next-btn", className: getClassName(nextClass, {
                            'cursor-not-allowed': isNextDisabled,
                            disabled: isNextDisabled,
                        }), disabled: isNextDisabled, onClick: clickNextPage },
                        React.createElement("span", { "data-testid": "span-5b677b51", className: "sr-only" }, t('NEXT')),
                        React.createElement(ChevronRightIcon, { className: "h-5 w-5", "aria-hidden": "true" })))))))));
}
Pagination.propTypes = {
    page: PropTypes.number,
    perPage: PropTypes.number,
    total: PropTypes.number,
    pageNeighbours: PropTypes.number,
    showCount: PropTypes.bool,
    onPageClick: PropTypes.func.isRequired,
    className: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.object,
    ]),
};
Pagination.defaultProps = {
    page: 1,
    perPage: 20,
    total: 0,
    showCount: true,
    pageNeighbours: 2,
};
